import { KeyboardBackspace as Arrow } from '@mui/icons-material';
import { useTheme } from '@mui/styles';
import {
  On3Icon5GoatsLogo,
  On3IconHerLogo,
  On3IconHighSchoolLogo,
  On3IconNilLogo,
  On3IconSoftballLogo,
  On3IconTransferPortal,
} from '@on3/icons';
import { Brands } from '@on3/ui-lib/components/Brands';
import { ServiceLogo } from '@on3/ui-lib/components/ServiceLogo/ServiceLogo';
import { On3, On3Bet, Rivals } from '@on3/ui-lib/components/Svg';
import { On3Recruit } from '@on3/ui-lib/components/Svg/On3Recruit';
import { useSite } from '@on3/ui-lib/index';
import { getAssetUrl } from '@on3/ui-lib/utils/images';
import {
  getHeaderTheme,
  getSiteChecks,
  getSiteTheme,
  HeaderThemes,
  SiteThemes,
} from '@on3/ui-lib/utils/sites';
import { Auth } from '@public/components/Header/Auth/Auth';
import { KsrHeader } from '@public/components/Header/Ksr';
import { LinkButton } from '@public/components/Header/LinkButton';
import { Menu } from '@public/components/Header/Menu';
import { MobileMenu } from '@public/components/Header/MobileMenu/MobileMenu';
import { ScrollWrapper } from '@public/components/Header/ScrollWrapper';
import { HeaderSearch } from '@public/components/Header/Search';
import { TrendingMenu } from '@public/components/Header/TrendingMenu';
import { SiteLogo } from '@public/components/Image/SiteLogo';
import { TeamAvatar } from '@public/components/Image/TeamAvatar';
import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { createContext, useCallback, useContext, useMemo } from 'react';

import styles, {
  colorBgDark,
  colorNeutral50,
  colorNeutral70,
  colorOn3,
  colorRivals,
} from './Base.module.scss';

type LogoType =
  | 'on3'
  | 'profile'
  | 'channel'
  | 'her'
  | 'nil'
  | 'pro'
  | 'softball'
  | 'team'
  | 'transferPortal'
  | 'recruits'
  | 'highSchool'
  | 'rivals'
  | 'bet'
  | null;

interface BaseHeaderProps {
  logo?: LogoType;
}

type ThemeKeys = keyof ReturnType<typeof getSiteChecks>;

interface IHeaderContext {
  logo: JSX.Element;
  secondaryLogo: JSX.Element | null;
  themes: {
    [key in ThemeKeys]: boolean;
  } & {
    active: LogoType;
    header: HeaderThemes;
    site: SiteThemes;
    style: {
      '--header-text-color'?: string;
      '--header-bg-color'?: string;
      '--site-primary-color'?: string;
      '--site-secondary-color'?: string;
      '--site-link-color'?: string;
    };
  };
}

const HeaderContext = createContext<IHeaderContext>({
  logo: <On3 />,
  secondaryLogo: null,
  themes: {
    isOn3: false,
    isKsr: false,
    isHer: false,
    isHighSchool: false,
    isNil: false,
    isPro: false,
    isSoftballAmerica: false,
    isTransferPortal: false,
    isChannel: false,
    isTeam: false,
    isPlayer: false,
    isCoach: false,
    isSearch: false,
    isNascar: false,
    isLogin: false,
    isAuth: false,
    isRecruits: false,
    isRivals: false,
    isRivalsHome: false,
    isRivalsTeam: false,
    isHighSchoolTeam: false,
    isBet: false,
    active: 'on3',
    header: HeaderThemes.BASE,
    site: SiteThemes.ON3,
    style: {},
  },
});

export const Header = ({ logo = 'on3' }: BaseHeaderProps) => {
  const { pathname, query } = useRouter();
  const { menuData, secondaryMenuData, trendingMenuData, currentSite } =
    useSite();
  const theme = useTheme();
  const siteChecks = getSiteChecks({ site: currentSite, pathname, query });
  const headerTheme = useMemo(() => getHeaderTheme(siteChecks), [siteChecks]);
  const siteTheme = useMemo(() => getSiteTheme(siteChecks), [siteChecks]);

  // Determine the logo to render
  const logos = useMemo(
    () => ({
      on3: {
        component: On3,
        attrs: { href: '/', title: 'Home' },
      },
      profile: {
        component: Rivals,
        attrs: { href: '/rivals/', title: 'Home' },
      },
      rivals: {
        component: Rivals,
        attrs: { href: '/rivals/', title: 'Home' },
      },
      nil: {
        component: On3IconNilLogo,
        attrs: { href: '/nil/', title: 'Home' },
      },
      pro: {
        component: On3Icon5GoatsLogo,
        attrs: { href: '/pro/', title: '5Goats Home' },
      },
      her: {
        component: On3IconHerLogo,
        attrs: { href: '/her/', title: "Women's Home" },
      },
      transferPortal: {
        component: On3IconTransferPortal,
        attrs: { href: '/transfer-portal/', title: 'Transfer Portal Home' },
      },
      highSchool: {
        component: On3IconHighSchoolLogo,
        attrs: { href: `/high-school/`, title: 'High School Home' },
      },
      recruits: {
        component: () => <On3Recruit className={styles.logo} isAlt />,
        attrs: { href: `/recruits/`, title: 'Rivals Recruits Home' },
      },
      team: {
        component: () => (
          <SiteLogo
            alternate={siteChecks.isRivalsTeam ? true : false}
            className={styles.logo}
          />
        ),
        attrs: { href: `${currentSite?.url}/`, title: 'Home' },
      },
      softball: {
        component: On3IconSoftballLogo,
        attrs: { href: '/softball/', title: 'Softball America Home' },
      },
      channel: {
        component: (props: { className?: string }) => (
          <div {...props}>
            {!!currentSite?.organization?.defaultAssetRes && (
              <TeamAvatar
                src={getAssetUrl(currentSite.organization.defaultAssetRes)}
              />
            )}
            <div className={styles.name}>
              <span>
                {currentSite?.organization?.knownAs ||
                  currentSite?.organization?.name ||
                  currentSite?.name}
              </span>
            </div>
          </div>
        ),
        attrs: { href: `${currentSite?.url}/`, title: 'Home' },
      },
      bet: {
        component: On3Bet,
        attrs: { href: '/bet/', title: 'Bet Home' },
      },
    }),
    [currentSite, siteChecks],
  );

  const primaryLogoType: LogoType = useMemo(() => {
    if (siteChecks.isPlayer || siteChecks.isCoach) return 'profile';
    if (siteChecks.isSearch) return 'rivals';
    if (siteChecks.isSoftballAmerica) return 'softball';
    if (siteChecks.isTeam || siteChecks.isRivalsTeam) return 'team';
    if (siteChecks.isPro) return 'pro';
    if (siteTheme === SiteThemes.RIVALS) return 'rivals'; // TODO: Rivals - swap to new logo
    if (siteChecks.isChannel) return null; // Channel logo is handled separately

    return 'on3';
  }, [siteChecks, siteTheme]);

  const secondaryLogoType: LogoType = useMemo(() => {
    if (siteChecks.isRecruits) return 'recruits';
    if (siteChecks.isChannel || siteChecks.isHighSchoolTeam) return 'channel';
    if (siteChecks.isHighSchool) return 'highSchool';
    if (siteChecks.isTransferPortal) return 'transferPortal';
    if (siteChecks.isHer) return 'her';
    if (siteChecks.isNil) return 'nil';
    if (siteChecks.isBet) return 'bet';

    return null;
  }, [siteChecks]);

  const Logo = useCallback(
    ({ type }: { type: LogoType | null }) => {
      if (!type) return null;

      const pageLogo = logos[type] || logos.on3;
      const Component = pageLogo.component;

      return (
        <Link
          className={styles.logoLink}
          href={pageLogo.attrs.href}
          title={pageLogo.attrs.title}
        >
          <Component
            className={clsx(styles.logo, {
              [styles.on3]: logo === 'on3',
            })}
          />
        </Link>
      );
    },
    [logo, logos],
  );

  // Inline styles based on the site data
  const themeStyles = useMemo(() => {
    if (siteChecks.isPro) {
      if (siteChecks.isNascar) {
        return {
          '--header-text-color': theme.appHeader.backgroundColor,
          '--header-bg-color': colorBgDark,
        };
      }

      return {
        '--header-text-color': colorBgDark,
        '--header-bg-color': theme.appHeader.backgroundColor,
      };
    }

    if (siteChecks.isTeam) {
      // For specific team sites, use black text color (UNC, etc.)
      if (currentSite.key === 432) {
        return {
          '--header-text-color': '#000',
          '--header-bg-color': theme.appHeader.backgroundColor ?? colorBgDark,
        };
      }

      return {
        '--header-text-color': '#fff',
        '--header-bg-color': theme.appHeader.backgroundColor ?? colorBgDark,
      };
    }

    if (siteChecks.isChannel) {
      return {
        '--header-text-color':
          currentSite.linkColor ?? currentSite.primaryColor ?? colorOn3,
        '--header-bg-color': '#fff',
      };
    }

    if (siteChecks.isHighSchoolTeam) {
      return {
        '--header-text-color': colorNeutral70,
        '--header-bg-color': '#fff',
      };
    }

    if (siteTheme === SiteThemes.RIVALS) {
      return {
        '--header-text-color': colorRivals,
        '--header-bg-color': '#fff',
      };
    }

    if (siteTheme === SiteThemes.ON3) {
      return {
        '--header-text-color': '#fff',
        '--header-bg-color': colorBgDark,
      };
    }

    return {
      '--header-bg-color': theme.appHeader.backgroundColor ?? '#fff',
    };
  }, [siteChecks, siteTheme, theme.appHeader, currentSite]);

  const siteStyles = useMemo(() => {
    switch (siteTheme) {
      case 'on3':
        return {
          '--site-primary-color': colorOn3,
          '--site-secondary-color': colorBgDark,
          '--site-link-color': colorOn3,
        };
      case 'rivals':
        return {
          '--site-primary-color': colorRivals,
          '--site-secondary-color': '#fff',
          '--site-link-color': colorRivals,
        };
      case 'site':
      default:
        return {
          '--site-primary-color': currentSite.primaryColor ?? colorNeutral70,
          '--site-secondary-color': currentSite.secondaryColor ?? '#fff',
          '--site-link-color':
            currentSite.linkColor ?? currentSite.primaryColor ?? colorNeutral70,
        };
    }
  }, [siteTheme, currentSite]);

  // Generate CSS variables from the theme style
  const themeCssVars = Object.entries({
    ...{
      '--on3-primary-color': colorOn3,
      '--rivals-primary-color': colorRivals,
    },
    ...themeStyles,
    ...siteStyles,
  })
    .map(([key, value]) => `${key}: ${value};`)
    .join(' ');
  const themeStyle = `:root { ${themeCssVars} }`;

  // Checks
  const showBrandsPrimary =
    siteChecks.isRivalsTeam || (siteChecks.isChannel && !siteChecks.isRecruits);
  const showBrandsSecondary =
    siteChecks.isTeam ||
    siteChecks.isRivalsTeam ||
    headerTheme === HeaderThemes.BRAND;
  const showLogoSecondary = !showBrandsSecondary && !!secondaryLogoType;
  const showHeaderSearchPrimary = headerTheme === HeaderThemes.PRIMARY;
  const showHeaderSearchSecondary = [
    HeaderThemes.SECONDARY,
    HeaderThemes.BRAND,
  ].includes(headerTheme);
  const showRecruitsCallout = headerTheme === HeaderThemes.PRIMARY;
  const showTeamsLink = siteChecks.isOn3 && siteTheme === SiteThemes.ON3;
  const showHighSchoolLink =
    siteChecks.isRivals && siteTheme === SiteThemes.RIVALS;
  const showLinkButton = showTeamsLink || showHighSchoolLink;
  const showSecondary = true;

  return (
    <HeaderContext.Provider
      value={{
        logo: <Logo type={primaryLogoType} />,
        secondaryLogo: <Logo type={secondaryLogoType} />,
        themes: {
          ...siteChecks,
          active: logo,
          header: headerTheme,
          site: siteTheme,
          style: {
            ...themeStyles,
            ...siteStyles,
          },
        },
      }}
    >
      <style
        dangerouslySetInnerHTML={{ __html: themeStyle }}
        data-id="theme-styles"
        type="text/css"
      />
      {siteChecks.isKsr ? (
        <KsrHeader />
      ) : (
        <>
          <ScrollWrapper>
            <header
              className={clsx(styles.base, {
                [styles.isChannel]:
                  siteChecks.isChannel && !siteChecks.isRecruits,
                [styles.isTeam]: siteChecks.isTeam,
                [styles.isSoftballAmerica]: siteChecks.isSoftballAmerica,
                [styles.isPro]: siteChecks.isPro,
                [styles.isNascar]: siteChecks.isNascar,
                [styles.isRecruits]: siteChecks.isRecruits,
                [styles.isRivals]: siteChecks.isRivals,
                [styles.isRivalsTeam]: siteChecks.isRivalsTeam,
                [styles.isHighSchoolTeam]: siteChecks.isHighSchoolTeam,
              })}
              data-header-theme={headerTheme}
              data-site-theme={siteTheme}
            >
              <div className={styles.primary}>
                <div className={styles.container}>
                  <MobileMenu data={menuData ?? secondaryMenuData ?? []} />

                  {showBrandsPrimary && (
                    <Brands
                      variant={siteChecks.isChannel ? 'default' : 'small'}
                    />
                  )}

                  {!!primaryLogoType && (
                    <div className={styles.logoWrapper}>
                      <Logo type={primaryLogoType} />
                    </div>
                  )}

                  {showHeaderSearchPrimary ? (
                    <HeaderSearch />
                  ) : (
                    <Menu data={menuData} />
                  )}

                  <Auth />
                </div>
              </div>
              {showSecondary && (
                <div className={styles.secondary}>
                  <div className={styles.container}>
                    {showLogoSecondary && (
                      <div className={styles.logoWrapper}>
                        <Logo type={secondaryLogoType} />
                      </div>
                    )}

                    {showBrandsSecondary && <Brands variant="small" />}

                    <Menu data={secondaryMenuData} isSecondary />

                    {showRecruitsCallout && (
                      <Link className={styles.recruits} href="/recruits/join/">
                        <ServiceLogo
                          className={styles.recruitsLogo}
                          fill={
                            siteChecks.isOn3 ? colorNeutral50 : colorNeutral70
                          }
                          fillAlt="currentColor"
                          isAlt
                          type="recruits"
                        />
                        <span className={styles.recruitsText}>
                          Get a profile. Be recruited.
                        </span>
                        <Arrow className={styles.arrow} />
                      </Link>
                    )}

                    {showHeaderSearchSecondary && (
                      <HeaderSearch
                        variant={
                          headerTheme === HeaderThemes.BRAND
                            ? 'link'
                            : 'default'
                        }
                      />
                    )}

                    {showLinkButton && (
                      <div data-menu-ui="link-button">
                        <LinkButton type={siteTheme} />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </header>
          </ScrollWrapper>
          {trendingMenuData && <TrendingMenu data={trendingMenuData} />}
        </>
      )}
    </HeaderContext.Provider>
  );
};

export const useHeader = () => {
  const context = useContext(HeaderContext);

  if (!context) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }

  return context;
};
